import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  Crown,
  Download,
  FileText,
  Filter,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConversionStrip } from "@/pages/admin/ConversionStrip";
import { ChaseEditor } from "@/pages/admin/ChaseEditor";
import { BulkActions, PromoteCohort2Button } from "@/pages/admin/BulkActions";
import { RecentActivity } from "@/pages/admin/RecentActivity";
import { RowActions } from "@/pages/admin/RowActions";
import { fmtDate } from "@/pages/admin/dates";
import { toast } from "sonner";

// Inline notes cell — saves on blur when the value has actually changed.
// Silent save (no toast churn); errors surface via toast in the parent.
function NotesCell({ row, onSave }) {
  const [val, setVal] = useState(row.notes || "");
  const [savedVal, setSavedVal] = useState(row.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Row refreshed from the server (e.g. status change refresh).
    setVal(row.notes || "");
    setSavedVal(row.notes || "");
  }, [row.notes]);

  const commit = async () => {
    if (val === savedVal) return;
    setSaving(true);
    try {
      await onSave(val);
      setSavedVal(val);
    } finally {
      setSaving(false);
    }
  };

  return (
    <textarea
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      placeholder="Add note…"
      rows={2}
      data-testid={`admin-notes-${row.email}`}
      className={`w-56 resize-none rounded-lg border bg-[#F7F5F2] p-2 text-xs leading-snug text-[#2A2724] placeholder:text-[#9a9a9a] focus:border-gold focus:outline-none ${
        saving ? "border-gold" : "border-black/10"
      }`}
    />
  );
}

const CITY_OPTIONS = ["Sydney", "Melbourne", "Brisbane", "Perth"];
const CATEGORY_OPTIONS = [
  "Hair",
  "Barber",
  "Makeup",
  "Brows & Lashes",
  "Skin & Injectables",
  "Spray Tans",
  "Nails",
  "Massage & Body",
  "Wellness",
];
const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "founder", label: "Founder" },
  { value: "archived", label: "Archived" },
];

// Radix Select doesn't accept empty-string values, so we sentinel "no filter".
const ALL = "__all";

const STATUS_STYLES = {
  new: "border-black/10 bg-[#F7F5F2] text-[#6B6B6B]",
  founder: "border-gold/60 bg-gold/10 text-[#8B6F1F]",
  archived: "border-black/10 bg-black/5 text-[#9a9a9a]",
};

export default function AdminWaitlist() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [city, setCity] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [status, setStatus] = useState("all");
  // Cohort filter: "" = ignore, "foundation_cohort_2" = only the second Sydney
  // cohort captured after Foundation sold out. Toggled by the gold chip in
  // the toolbar.
  const [cohort, setCohort] = useState("");
  // Filter chip that shows only unsigned founders whose partner-terms link
  // has been sitting untouched for longer than the nudge cadence — Kate /
  // Sophie's "outstanding chases" queue.
  const [needsChasing, setNeedsChasing] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef(null);
  const [busyRow, setBusyRow] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  // Signoff watchdog — locks the bulk 'Mark as Founder' action until the
  // solicitor has pasted their green-light note into legal_signoff.md.
  const [legalSigned, setLegalSigned] = useState(false);
  // Founder-signature live feed (poll every 20s while the CRM is open).
  const eventsSinceRef = useRef(new Date().toISOString());

  // Debounce keystrokes so we don't hammer the API on every letter.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search]);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (city !== ALL) q.set("city", city);
    if (category !== ALL) q.set("category", category);
    if (status && status !== "all") q.set("status", status);
    if (cohort) q.set("cohort", cohort);
    if (needsChasing) q.set("needs_chasing", "true");
    if (debouncedSearch) q.set("q", debouncedSearch);
    return q.toString();
  }, [city, category, status, cohort, needsChasing, debouncedSearch]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/waitlist${query ? `?${query}` : ""}`);
      setRows(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load waitlist.";
      setError(typeof msg === "string" ? msg : "Failed to load waitlist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Fetch the legal signoff status on mount so the bulk 'Mark as Founder'
  // button knows whether it's allowed to render.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/legal-signoff-status");
        setLegalSigned(Boolean(data?.signed));
      } catch {
        // Fail closed — treat as unsigned rather than silently unlocking.
      }
    })();
  }, []);

  // Poll for freshly signed founders every 20s so Kate/Sophie feel each win
  // as it lands. The endpoint is cheap (single indexed query on
  // `terms_signed_at`) and the payload is tiny.
  useEffect(() => {
    let cancelled = false;
    const pollOnce = async () => {
      try {
        const { data } = await api.get(
          `/admin/founder-events?since=${encodeURIComponent(eventsSinceRef.current)}`
        );
        if (cancelled) return;
        eventsSinceRef.current = data.as_of || eventsSinceRef.current;
        (data.events || []).forEach((ev) => {
          toast.success(
            `${ev.business_name || "A founder"} just signed the partner terms`,
            {
              description: `${ev.city || "Sydney"} · ${new Date(ev.signed_at).toLocaleTimeString()}`,
              icon: "🎉",
              duration: 8000,
            }
          );
        });
      } catch {
        // Silent — polling is best-effort.
      }
    };
    const t = setInterval(pollOnce, 20000);
    // Fire once immediately so admins don't have to wait a full poll cycle
    // for the toast on a signing that landed seconds before they opened the CRM.
    pollOnce();
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const setRowStatus = async (row, next) => {
    setBusyRow(row.id);
    try {
      await api.patch(`/admin/waitlist/${row.id}`, { status: next });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      const messages = {
        founder: `${row.business_name} marked as Founder`,
        archived: `${row.business_name} archived`,
        new: `${row.business_name} moved back to the queue`,
      };
      toast.success(messages[next] || "Updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyRow(null);
    }
  };

  const setRowNotes = async (row, notes) => {
    // Silent save — no toast on every keystroke blur.
    try {
      await api.patch(`/admin/waitlist/${row.id}`, { notes });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, notes: notes.trim() || null } : r)));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Notes save failed";
      toast.error(typeof msg === "string" ? msg : "Notes save failed");
    }
  };

  const markTermsSigned = async (row) => {
    setBusyRow(row.id);
    try {
      await api.post(`/admin/waitlist/${row.id}/terms-signed`);
      setRows((rs) =>
        rs.map((r) =>
          r.id === row.id ? { ...r, terms_signed_at: new Date().toISOString() } : r
        )
      );
      toast.success(`${row.business_name} — terms signed`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyRow(null);
    }
  };

  const filterContext = useMemo(() => {
    const bits = [];
    if (city !== ALL) bits.push(city);
    if (category !== ALL) bits.push(category);
    if (status !== "all") bits.push(status);
    if (debouncedSearch) bits.push(`"${debouncedSearch}"`);
    return bits.join(" · ");
  }, [city, category, status, debouncedSearch]);

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectAllVisible = (checked) =>
    setSelected(checked ? new Set(rows.map((r) => r.id)) : new Set());
  const clearSelection = () => setSelected(new Set());

  // Called by the extracted BulkActions after a successful bulk PATCH so
  // the table can update its local rows without a full refetch.
  const applyBulkUpdate = (ids, next) => {
    const idSet = new Set(ids);
    setRows((rs) => rs.map((r) => (idSet.has(r.id) ? { ...r, status: next } : r)));
  };

  const downloadWeeklyPdf = async () => {
    try {
      const res = await api.get("/admin/digest/weekly.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `book-away-weekly-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Weekly PDF downloaded");
    } catch {
      toast.error("Failed to download weekly PDF");
    }
  };



  const downloadCsv = async () => {
    try {
      const res = await api.get(
        `/admin/waitlist.csv${query ? `?${query}` : ""}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `book-away-waitlist-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F5F2]">
        <div className="flex items-center gap-3 text-[#6B6B6B]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-gold" />
          <span className="text-sm uppercase tracking-[0.28em]">Loading</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin/waitlist" }} />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-16 md:px-12">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="overline mb-2">Book Away · admin</p>
            <h1 className="font-serif text-4xl font-light text-[#2A2724] md:text-5xl">
              Waitlist <span className="italic text-gold">CRM</span>
            </h1>
            <p className="mt-3 max-w-xl text-sm text-[#6B6B6B]">
              Every business that has raised their hand. Filter, search and
              hand-pick your Foundation Members directly from the table.
            </p>
          </div>
          <div className="flex gap-3">
            <a
              data-testid="admin-audit-log-link"
              href="/admin/audit-log"
              className="inline-flex items-center rounded-md border border-black/10 bg-white px-4 py-2 text-sm text-[#2A2724] transition-colors hover:border-gold hover:text-gold"
            >
              Audit log →
            </a>
            <Button
              data-testid="admin-refresh"
              onClick={load}
              variant="outline"
              className="border-black/10 bg-white text-[#2A2724] hover:border-gold hover:text-gold"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              data-testid="admin-csv"
              onClick={downloadCsv}
              className="rounded-full bg-gold text-black hover:bg-[#AA8627]"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              data-testid="admin-weekly-pdf"
              onClick={downloadWeeklyPdf}
              variant="outline"
              className="rounded-full border-gold/40 bg-white text-[#8B6F1F] hover:bg-gold/10"
              title="Print-ready weekly digest — share with investors"
            >
              <FileText className="mr-2 h-4 w-4" />
              Weekly PDF
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-black/10 bg-white p-4">
          {/* Search */}
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9a9a]" />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business, owner, or email…"
              className="border-black/10 bg-[#F7F5F2] pl-9 text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
            />
            {search && (
              <button
                type="button"
                data-testid="admin-search-clear"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#9a9a9a] hover:bg-black/5 hover:text-[#2A2724]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#6B6B6B]">
            <Filter className="h-3.5 w-3.5 text-gold" />
            Filters
          </span>

          <Select value={city} onValueChange={setCity}>
            <SelectTrigger
              data-testid="admin-filter-city"
              className="w-[160px] border-black/10 bg-[#F7F5F2]"
            >
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All cities</SelectItem>
              {CITY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="admin-filter-category"
              className="w-[190px] border-black/10 bg-[#F7F5F2]"
            >
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All categories</SelectItem>
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              data-testid="admin-filter-status"
              className="w-[160px] border-black/10 bg-[#F7F5F2]"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Cohort-2 toggle — surfaces Sydney leads captured after Foundation sold out. */}
          <button
            type="button"
            data-testid="admin-filter-cohort2"
            data-active={cohort === "foundation_cohort_2"}
            onClick={() =>
              setCohort(cohort === "foundation_cohort_2" ? "" : "foundation_cohort_2")
            }
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              cohort === "foundation_cohort_2"
                ? "border-gold bg-gold text-black"
                : "border-gold/40 bg-[#F7F5F2] text-[#8B6F1F] hover:bg-gold/10"
            }`}
          >
            <Crown className="h-3.5 w-3.5" />
            Cohort 2
          </button>

          <PromoteCohort2Button
            active={cohort === "foundation_cohort_2"}
            onDone={load}
          />

          {/* Needs-chasing toggle — unsigned founders past the 3-day nudge
              cadence. Overrides the status filter server-side. */}
          <button
            type="button"
            data-testid="admin-filter-needs-chasing"
            data-active={needsChasing}
            onClick={() => setNeedsChasing((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              needsChasing
                ? "border-[#8B6F1F] bg-[#FDF6E3] text-[#8B6F1F]"
                : "border-black/10 bg-[#F7F5F2] text-[#6B6B6B] hover:bg-[#EDEAE4]"
            }`}
          >
            <Clock className="h-3.5 w-3.5" />
            Needs chasing
          </button>

          <ChaseEditor visible={needsChasing} />

          <span
            data-testid="admin-count"
            className="ml-auto text-xs uppercase tracking-[0.24em] text-[#6B6B6B]"
          >
            {loading ? "Loading…" : `${rows.length} lead${rows.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {error && (
          <p data-testid="admin-error" className="mb-4 text-sm text-red-600">
            {error}
          </p>
        )}

        <ConversionStrip />

        <RecentActivity rows={rows} filterContext={filterContext} />

        {/* Bulk-action bar — appears when at least one row is checked */}
        <BulkActions
          selected={selected}
          legalSigned={legalSigned}
          onDone={applyBulkUpdate}
          onClearSelection={clearSelection}
        />

        <div className="overflow-hidden rounded-2xl border border-black/10 bg-white">
          <table className="min-w-full text-sm" data-testid="admin-waitlist-table">
            <thead className="bg-[#EDEAE4] text-[#2A2724]">
              <tr>
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    data-testid="admin-select-all"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={(v) => selectAllVisible(Boolean(v))}
                    aria-label="Select all rows"
                  />
                </th>
                {[
                  "Created",
                  "Status",
                  "Business",
                  "Owner",
                  "Email",
                  "City",
                  "Category",
                  "Phone",
                  "Instagram",
                  "Notes",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[0.7rem] font-medium uppercase tracking-[0.2em]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-14 text-center text-sm text-[#9a9a9a]"
                  >
                    No leads match these filters yet.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const s = r.status || "new";
                  const isFounder = s === "founder";
                  const isArchived = s === "archived";
                  const busy = busyRow === r.id;
                  const isSelected = selected.has(r.id);
                  return (
                    <tr
                      key={r.id || r._id || r.email}
                      data-testid={`admin-row-${r.email}`}
                      className={`border-t border-black/5 text-[#2A2724] transition-colors ${
                        isSelected ? "bg-gold/10" : "hover:bg-[#F7F5F2]"
                      }`}
                    >
                      <td className="px-4 py-3 align-top">
                        <Checkbox
                          data-testid={`admin-select-${r.email}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(r.id)}
                          aria-label={`Select ${r.business_name}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B6B6B]">
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`admin-status-${r.email}`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.14em] ${STATUS_STYLES[s]}`}
                        >
                          {isFounder && <Crown className="h-3 w-3" />}
                          {s}
                        </span>
                        {isFounder && r.terms_signed_at && (
                          <span
                            data-testid={`admin-founder-signed-${r.email}`}
                            title={`Terms signed ${fmtDate(r.terms_signed_at)}`}
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#E9F5EC] px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[#2f7d3f]"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Signed
                          </span>
                        )}
                        {isFounder && !r.terms_signed_at && (
                          <span
                            data-testid={`admin-founder-unsigned-${r.email}`}
                            title={
                              r.nudged_at
                                ? `Nudged ${fmtDate(r.nudged_at)} — still no signature`
                                : "Founder hasn't signed the partner terms yet"
                            }
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#FDF6E3] px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[#8B6F1F]"
                          >
                            <Clock className="h-3 w-3" />
                            {r.nudged_at ? "Nudged" : "Awaiting"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        <span>{r.business_name}</span>
                        {r.cohort === "foundation_cohort_2" && (
                          <span
                            data-testid={`admin-cohort-badge-${r.email}`}
                            title="Joined the waitlist after Sydney's first Foundation cohort sold out"
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[#8B6F1F]"
                          >
                            <Crown className="h-2.5 w-2.5" />
                            Cohort 2
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.contact_name}</td>
                      <td className="px-4 py-3 text-[#6B6B6B]">{r.email}</td>
                      <td className="px-4 py-3">{r.city || "—"}</td>
                      <td className="px-4 py-3">{r.business_type || "—"}</td>
                      <td className="px-4 py-3 text-[#6B6B6B]">{r.phone || "—"}</td>
                      <td className="px-4 py-3 text-[#6B6B6B]">
                        {r.instagram || "—"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <NotesCell row={r} onSave={(v) => setRowNotes(r, v)} />
                      </td>
                      <td className="px-4 py-3">
                        <RowActions
                          row={r}
                          busy={busy}
                          onSetStatus={(next) => setRowStatus(r, next)}
                          onMarkTermsSigned={() => markTermsSigned(r)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
